/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import type { Task, TaskModel } from '@repo/common/types/Task';
import { TaskPriority, TaskStatus } from '@repo/common/types/Task';
import type {
  Transcript,
  TranscriptFull } from '@repo/common/types/Transcript';
import { derived, get, writable, type Readable } from 'svelte/store';

import { getHandlerAsync } from '../sharedlib';

// Import the UI Task type for mapping
import type { UITask } from '@repo/common/types/Task';
import { getTranscriptName } from '@repo/toolslib/src/utils/transcript.util';
import type { Workspace } from '@repo/common/types/Workspace';

export interface TaskStore {
  tasks: Map<string, Task>;
  loading: boolean;
  error: string | null;
  lastSync: Date | null;
  lastSyncedTranscriptId?: string | null;
}

const createTaskStore = () => {
  const internalStore = writable<TaskStore>({
    tasks: new Map(),
    loading: false,
    error: null,
    lastSync: null
  });

  const { subscribe, update } = internalStore;

  let taskModel: TaskModel | null = null;
  let syncInProgress = false;

  // Helper to safely get weight from task data (handles both object and string cases)
  const getTaskWeight = (task: Task): number => {
    return task.data?.weight ?? 0;
  };

  const initializeTaskModel = async () => {
    if (!taskModel) {
      try {
        const handler = await getHandlerAsync();
        taskModel = await handler.getTaskModel();
      } catch (err) {
        console.warn('Failed to initialize task model:', err);
        throw err;
      }
    }
    return taskModel;
  };

  const syncTasksFromTranscripts = async (
    transcripts: Array<[string, TranscriptFull]>
  ): Promise<{ created: number; deleted: number; existing: number }> => {
    if (syncInProgress) {
      console.log('Task sync already in progress, skipping...');
      return { created: 0, deleted: 0, existing: 0 };
    }

    try {
      syncInProgress = true;
      update((store) => ({ ...store, loading: true, error: null }));

      const model = await initializeTaskModel();
      const transcriptArray = transcripts.map(([_, transcript]) => transcript);

      // Sync tasks from transcripts
      const { created, deleted, existing } = await model.syncFromTranscripts(transcriptArray);

      // Refresh tasks
      const allTasks = (await model?.findAllObj({})) || [];

      update((store) => ({
        ...store,
        tasks: new Map(allTasks.map((task: any) => [task.id, task])),
        loading: false,
        lastSync: new Date()
      }));

      console.log(
        `Task sync completed: ${created} created, ${deleted} deleted, ${existing} existing`
      );
      return { created, deleted, existing };
    } catch (err: any) {
      console.warn('Failed to sync tasks:', err);
      update((store) => ({
        ...store,
        loading: false,
        error: err.message
      }));
      return { created: 0, deleted: 0, existing: 0 };
    } finally {
      syncInProgress = false;
    }
  };

  let lastRefreshTime = 0;
  let refreshTasksPromise: Promise<void> | null = null;
  const REFRESH_INTERVAL = 15_000; // 15 seconds

  const refreshTasks = async (force = false): Promise<void> => {
    if (refreshTasksPromise) {
      return refreshTasksPromise;
    }

    const shouldSkip =
      !force &&
      lastRefreshTime > 0 &&
      Date.now() - lastRefreshTime < REFRESH_INTERVAL &&
      get(internalStore).tasks.size > 0;
    if (shouldSkip) {
      return Promise.resolve();
    }

    // Check if database initialization is in progress - if so, skip refresh to avoid deadlock
    // The refresh will happen automatically after initialization completes via asyncLoadDB
    const handler = await getHandlerAsync();
    if (!handler.isDatabaseLoaded()) {
      // Return resolved promise - refresh will happen after initialization via asyncLoadDB
      return Promise.resolve();
    }

    refreshTasksPromise = (async () => {
      try {
        update((store) => ({ ...store, loading: true, error: null }));

        const handler = await getHandlerAsync();

        // Double-check database is loaded before proceeding (should be true at this point)
        if (!handler.isDatabaseLoaded()) {
          console.warn('[task.store] Database not loaded in refreshTasks, skipping');
          return;
        }

        // getTaskModel() will handle waiting for initialization if needed
        // It checks for cached models first, then waits for in-flight promise if initialization is in progress
        const taskModel = await handler.getTaskModel();
        const allTasks = await taskModel.findAllObj();

        // Sort tasks by weight (with createdAt as tiebreaker) before storing
        const sortedTasks = allTasks.sort((a, b) => {
          const weightA = getTaskWeight(a);
          const weightB = getTaskWeight(b);
          if (weightA !== weightB) {
            return weightA - weightB;
          }
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateA - dateB;
        });

        update((store) => ({
          ...store,
          tasks: new Map(sortedTasks.map((task: Task) => [task.id, task])),
          loading: false,
          error: null,
          lastSync: new Date()
        }));

        console.log('task 0 data', allTasks?.[0]?.data);
        console.log('task 0 data typeof', typeof allTasks?.[0]?.data);


        lastRefreshTime = Date.now();
      } catch (err: any) {
        if (err?.message?.includes('User ID not set')) {
          console.debug('Could not load tasks - user ID not set');
        } else {
          console.warn('Error refreshing tasks:', err);
          update((store) => ({
            ...store,
            loading: false,
            error: err.message
          }));
        }
      } finally {
        refreshTasksPromise = null;
      }
    })();

    return refreshTasksPromise;
  };

  const refreshTasksByTranscript = async (transcriptId: string) => {
    try {
      update((store) => ({ ...store, loading: true, error: null }));

      const handler = await getHandlerAsync();
      const taskModel = await handler.getTaskModel();

      // Use raw SQL method to get tasks for specific transcript
      const transcriptTasks = await taskModel.getTasksByTranscriptRaw(transcriptId);
      if (transcriptTasks && transcriptTasks.length > 0) {
        transcriptTasks.sort((a: Task, b: Task) => {
          const weightA = getTaskWeight(a);
          const weightB = getTaskWeight(b);
          return weightA - weightB;
        });
      }
      console.log(`Tasks for transcript ${transcriptId}:`, transcriptTasks.length);
      console.log('Transcript weights:', transcriptTasks.map((task: Task) => getTaskWeight(task)));
      
      // Update store with only tasks for this transcript
      update((store) => {
        const newTasks = new Map(store.tasks);

        // Remove existing tasks for this transcript
        for (const [taskId, task] of store.tasks.entries()) {
          if (task.memoryId === transcriptId) {
            newTasks.delete(taskId);
          }
        }

        // Add new tasks for this transcript
        transcriptTasks.forEach((task: Task) => {
          newTasks.set(task.id, task);
        });

        return {
          ...store,
          tasks: newTasks,
          loading: false,
          error: null,
          lastSync: new Date(),
          lastSyncedTranscriptId: transcriptId
        };
      });


    } catch (err: any) {
      if (err?.message?.includes('User ID not set')) {
        console.log('Could not load tasks - user ID not set');
      } else {
        console.warn('Error refreshing tasks for transcript:', err);
        update((store) => ({
          ...store,
          loading: false,
          error: err.message
        }));
      }
    }
  };

  const createUITask = (
    taskList: UITask[],
    afterId: string,
    memoryId?: string
  ): { task: UITask | null; newTaskId: string } => {
    const currentTaskIndex = taskList.findIndex((task) => task.id === afterId);
    const currentTask = taskList[currentTaskIndex];
    console.log(`Creating after ${currentTask.title}`);

    const now = new Date();
    const newTask: UITask = {
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now,
      dueDate: undefined,
      title: '',
      status: TaskStatus.TODO,
      workspace: currentTask.workspace,
      transcript: currentTask.transcript,
      label: taskList[currentTaskIndex].label,
      priority: undefined,
      weight: currentTask.weight + 50 // Insert between current and next task (using increments of 100)
    };
    const task = mapUITaskToTask(newTask, undefined, memoryId);
    createTask(task);

    return { task: newTask, newTaskId: newTask.id };
  };

  const createTask = (taskData: Partial<Task>): Task | null => {
    try {
      const task: Task = {
        id: taskData.id || crypto.randomUUID(),
        name: taskData.name || '',
        contentHash: taskData.contentHash,
        description: taskData.description,
        status: taskData.status || TaskStatus.TODO,
        priority: taskData.priority || undefined,
        tags: taskData.tags || [],
        createdAt: taskData.createdAt || new Date(),
        dueDate: taskData.dueDate,
        updatedAt: new Date(),
        completedAt: taskData.completedAt,
        deletedAt: taskData.deletedAt,
        memoryId: taskData.memoryId,
        workspaceId: taskData.workspaceId,
        data: taskData.data
      };

      // Update store immediately for better UX
      setTask(task);

      console.log('getAllTasks', get(getAllTasks()));

      return task;
    } catch (err: any) {
      console.warn('Failed to create task:', err);
      update((store) => ({ ...store, error: err.message }));
      return null;
    }
  };

  const updateTask = async (taskId: string, updates: Partial<Task>): Promise<boolean> => {
    try {
      console.log('updateTask', taskId, updates);
      const model = await initializeTaskModel();
      const task = getTask(taskId);

      if (!task) {
        throw new Error(`Task with id ${taskId} not found`);
      }

      const updatedTask = { ...task, ...updates, updatedAt: new Date() };
      await model?.updateItem(updatedTask);

      // Update store immediately for better UX
      setTask(updatedTask);

      // Note: syncUITaskStore() not called here - reactive stores handle updates automatically

      return true;
    } catch (err: any) {
      console.warn('Failed to update task:', err);
      update((store) => ({ ...store, error: err.message }));
      return false;
    }
  };

  const deleteTask = async (taskId: string): Promise<boolean> => {
    try {
      const model = await initializeTaskModel();
      await (model as any).destroyRecords({ id: taskId });

      // Update store immediately for better UX
      removeTask(taskId);

      // Note: syncUITaskStore() not called here - reactive stores handle updates automatically

      return true;
    } catch (err: any) {
      console.warn('Failed to delete task:', err);
      update((store) => ({ ...store, error: err.message }));
      return false;
    }
  };

  const deleteByMemory = async (memoryId: string): Promise<boolean> => {
    try {
      const model = await initializeTaskModel();
      await model?.deleteByMemory(memoryId);

      // Update store immediately for better UX - remove all tasks for this memory
      update((store) => {
        const newTasks = new Map(store.tasks);
        for (const [taskId, task] of store.tasks.entries()) {
          if (task.memoryId === memoryId) {
            newTasks.delete(taskId);
          }
        }
        return { ...store, tasks: newTasks };
      });

      return true;
    } catch (err: any) {
      console.warn('Failed to delete tasks by memory:', err);
      update((store) => ({ ...store, error: err.message }));
      return false;
    }
  };

  const getAllTasks = (): Readable<Task[]> => {
    return derived({ subscribe }, ($store) => {
      return Array.from($store.tasks.values());
    });
  };

  const getTasksByStatus = (status: TaskStatus[]): Readable<Task[]> => {
    return derived({ subscribe }, ($store) => {
      return Array.from($store.tasks.values()).filter((task) => status.includes(task.status));
    });
  };

  const getTasksByPriority = (priority: TaskPriority[]): Readable<Task[]> => {
    return derived({ subscribe }, ($store) => {
      return Array.from($store.tasks.values()).filter(
        (task) => task.priority && priority.includes(task.priority)
      );
    });
  };

  const getTodayTasks = (): Readable<Task[]> => {
    return derived({ subscribe }, ($store) => {
      const today = new Date();
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);

      return Array.from($store.tasks.values()).filter((task) => {
        if (!task.dueDate) return false;
        const dueDate = new Date(task.dueDate);
        return dueDate >= todayStart && dueDate < todayEnd;
      });
    });
  };

  const getUpcomingTasks = (): Readable<Task[]> => {
    return derived({ subscribe }, ($store) => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStart = new Date(
        tomorrow.getFullYear(),
        tomorrow.getMonth(),
        tomorrow.getDate()
      );

      return Array.from($store.tasks.values())
        .filter((task) => {
          if (!task.dueDate) return false;
          const dueDate = new Date(task.dueDate);
          return dueDate >= tomorrowStart;
        })
        .sort((a, b) => {
          const dateA = new Date(a.dueDate!);
          const dateB = new Date(b.dueDate!);
          return dateA.getTime() - dateB.getTime();
        });
    });
  };

  // Helper methods for task management
  const getTask = (taskId: string): Task | undefined => {
    return get(internalStore).tasks.get(taskId);
  };

  const setTask = (task: Task) => {
    update((store) => {
      const newTasks = new Map(store.tasks);
      newTasks.set(task.id, task);

      // Sort tasks to ensure order is preserved
      const sortedTasks = Array.from(newTasks.values()).sort((a, b) => {
        const weightA = getTaskWeight(a);
        const weightB = getTaskWeight(b);
        // Primary sort by weight
        if (weightA !== weightB) {
          return weightA - weightB;
        }
        // Secondary sort by createdAt to ensure stable ordering when weights are equal
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      });

      const sortedMap = new Map(sortedTasks.map((t) => [t.id, t]));
      return { ...store, tasks: sortedMap };
    });
  };

  const removeTask = (taskId: string) => {
    update((store) => {
      const newTasks = new Map(store.tasks);
      newTasks.delete(taskId);
      return { ...store, tasks: newTasks };
    });
  };

  const updateTaskInStore = (taskId: string, updates: Partial<Task>) => {
    update((store) => {
      const task = store.tasks.get(taskId);
      if (!task) return store;

      const newTasks = new Map(store.tasks);
      newTasks.set(taskId, { ...task, ...updates, updatedAt: new Date() });
      return { ...store, tasks: newTasks };
    });
  };

  const updateMultipleTasks = async (updates: Partial<UITask>[]): Promise<boolean> => {
    try {
      const model = await initializeTaskModel();
      if (!model) {
        throw new Error('Task model not initialized');
      }
      const tasksToUpsert: Task[] = [];
      let hasChanges = false;

      // Update tasks in place to preserve order
      update((store) => {
        const newTasks = new Map(store.tasks);

        updates.forEach((taskUpdate) => {
          const task = store.tasks.get(taskUpdate.id!);
          if (task) {
            // Use the utility method to map UITask back to Task
            const mappedUpdates = mapUITaskToTask(taskUpdate, task);
            const updatedTask = { ...task, ...mappedUpdates, updatedAt: new Date() };

            newTasks.set(taskUpdate.id!, updatedTask);
            tasksToUpsert.push(updatedTask);
            hasChanges = true;
          }
        });

        // Sort tasks to ensure order is preserved
        const sortedTasks = Array.from(newTasks.values()).sort((a, b) => {
          const weightA = getTaskWeight(a);
          const weightB = getTaskWeight(b);
          // Primary sort by weight
          if (weightA !== weightB) {
            return weightA - weightB;
          }
          // Secondary sort by createdAt to ensure stable ordering when weights are equal
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateA - dateB;
        });

        const sortedMap = new Map(sortedTasks.map((t) => [t.id, t]));
        return { ...store, tasks: sortedMap };
      });

      if (hasChanges && tasksToUpsert.length > 0) {
        // Persist to database
        await model?.bulkUpsertItems(tasksToUpsert);
      }

      // Note: We don't call syncUITaskStore() here because:
      // 1. The reactive stores (allUITasks) will automatically update via taskStore subscription
      // 2. Calling syncUITaskStore() without transcript context syncs ALL tasks (breaking filters)
      // 3. Components should manage their own UI sync with appropriate filters

      return true;
    } catch (err: any) {
      console.warn('Failed to update multiple tasks:', err);
      update((store) => ({ ...store, error: err.message }));
      return false;
    }
  };

  const getTasksArray = (): Readable<Task[]> => {
    return derived({ subscribe }, ($store) => {
      return Array.from($store.tasks.values());
    });
  };

  // Get a specific task reactively
  const getTaskReactive = (taskId: string): Readable<Task | undefined> => {
    return derived({ subscribe }, ($store) => {
      return $store.tasks.get(taskId);
    });
  };


  return {
    subscribe,
    syncTasksFromTranscripts,
    refreshTasks,
    refreshTasksByTranscript,
    createTask,
    createUITask,
    updateTask,
    deleteTask,
    deleteByMemory,
    getTasksByStatus,
    getTasksByPriority,
    getTodayTasks,
    getUpcomingTasks,
    getTask,
    setTask,
    removeTask,
    updateTaskInStore,
    updateMultipleTasks,
    getAllTasks,
    getTasksArray,
    getTaskReactive
  };
};


// Map UI Task objects back to Task format
// TODO: Improve the performance by reducing how often this is called
export const mapUITaskToTask = (
  uiTask: Partial<UITask>,
  existingTask?: Task,
  memoryId?: string
): Partial<Task> => {
  const mappedTask: Partial<Task> = {
    id: uiTask.id
  };

  // Only map fields that are actually provided in the uiTask
  if ('title' in uiTask) {
    mappedTask.name = uiTask.title; // Map title back to name
  }

  if ('label' in uiTask) {
    mappedTask.description = uiTask.label; // Map label back to description
  }

  if ('status' in uiTask) {
    mappedTask.status = uiTask.status as TaskStatus; // Cast to proper type
  }

  if ('priority' in uiTask) {
    mappedTask.priority = uiTask.priority as TaskPriority; // Cast to proper type
  }

  if ('dueDate' in uiTask) {
    mappedTask.dueDate = uiTask.dueDate;
  }

  if ('workspace' in uiTask && uiTask.workspace?.id && uiTask.workspace.id !== '') {
    mappedTask.workspaceId = uiTask.workspace.id;
  } else if ('workspace' in uiTask && !uiTask.workspace?.id) {
    mappedTask.workspaceId = undefined;
  }

  if ('transcript' in uiTask && uiTask.transcript?.id && uiTask.transcript.id !== '') {
    mappedTask.memoryId = uiTask.transcript.id;
  } else if ('transcript' in uiTask && !uiTask.transcript?.id) {
    mappedTask.memoryId = undefined;
  }

  // Handle weight updates in the data object
  if ('weight' in uiTask) {
    // Parse existing data if it's a string
    let existingData = existingTask?.data;
    if (typeof existingData === 'string') {
      try {
        existingData = JSON.parse(existingData);
      } catch {
        existingData = {};
      }
    }

    mappedTask.data = {
      ...(existingData || {}),
      weight: uiTask.weight
    };
  }

  if (memoryId) {
    mappedTask.memoryId = memoryId;
  }

  // console.log('mapUITaskToTask - mappedTask:', uiTask, existingTask, mappedTask);

  return mappedTask;
};
export const mapTaskToUITask = (index: number, task: Task, transcript?: Transcript | undefined, workspace?: Workspace | undefined): UITask => {
  return {
    id: task.id,
    title: task?.name || '',
    label: task.description || '',
    status: task.status,
    priority: task.priority ? task.priority : undefined,
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
    dueDate: task.dueDate,
    transcript: {
      id: transcript?.id || task.memoryId || '',
      name: getTranscriptName(transcript)
    },
    workspace: {
      id: task.workspaceId || '',
      name: workspace?.name || ''
    },
    weight: task.data?.weight || index + 1
  };
};

export const taskStore = createTaskStore();
